import { recordHubStatus } from '@/features/internal/hub/hubStatusSlice'
import { selectActiveTour } from '@/features/tours/engine/tours-slice'
import {
  HUB_SOURCE_FALLBACK,
  HUB_SOURCE_HEADER,
  HUB_SYNCED_AT_HEADER
} from '@/lib/hub/fallback-headers'
import {
  bodyMentionsPracticeId,
  PRACTICE_BLOCKED_CODE,
  PRACTICE_INTERCEPTED_MESSAGE,
  urlMentionsPracticeId
} from '@/lib/tours/practice-ids'
import type { RootState, AppDispatch } from '@/stores/store'
import type { HubReadEnvelope } from '@/types/hub-fallback'
import { getAppArea } from '@/utils/analytics/app-area'
import {
  READ_ONLY_IMPERSONATION_CODE,
  READ_ONLY_IMPERSONATION_MESSAGE
} from '@/utils/auth/impersonation'

import { useDispatch, useSelector } from 'react-redux'

import {
  type FetchBaseQueryError,
  fetchBaseQuery,
  retry
} from '@reduxjs/toolkit/query'
import { toast } from 'sonner'

// Use throughout your app instead of plain `useDispatch` and `useSelector`
export const useAppDispatch = useDispatch.withTypes<AppDispatch>()
export const useAppSelector = useSelector.withTypes<RootState>()

// Enhanced base query with smart retry logic.
//
// X-Atlas-Surface: tells the server which product area the request came from
// (portal / internal / technician / dispatching / sales / …) so server-side
// Prometheus counters can label `surface` without the client having to pass
// it explicitly. Derived from window.location.pathname via the same helper
// GA uses for `app_area`, keeping the two telemetry layers in lockstep.
const baseQuery = fetchBaseQuery({
  baseUrl: '/api',
  credentials: 'include',
  prepareHeaders: (headers) => {
    if (typeof window !== 'undefined') {
      headers.set('x-atlas-surface', getAppArea(window.location.pathname))
    }
    return headers
  }
})

// Detects responses from hub-direct READ routes that returned a
// `HubReadEnvelope` shape and dispatches the freshness state to the
// hub-status slice. Runs once per fulfilled request, regardless of which
// RTK Query endpoint owns the call.
function maybeRecordHubStatus(
  args: Parameters<typeof baseQuery>[0],
  api: Parameters<typeof baseQuery>[1],
  data: unknown,
  response?: Response
) {
  const url = typeof args === 'string' ? args : args.url
  // The IP and the route family that follows it: /api/hub/<ip>/<family>/…
  // The family is what the status is filed under, so a hub that is broken on
  // one of its endpoints does not read as down on the pages served by the
  // others. See hubStatusSlice.
  const match = url.match(/\/hub\/([^/?#]+)\/([^/?#]+)/)
  const ip = match?.[1] ? decodeURIComponent(match[1]) : null
  const resource = match?.[2] ? decodeURIComponent(match[2]) : null
  if (!ip || !resource) return

  // Routes that return the hub's RAW shape (guide lineups, video configs)
  // cannot carry an envelope, so they mark a mirrored body with a header
  // instead. Without this the client believed a cached body was live whenever
  // the probe said the hub was up — and its Save wrote those cached values
  // back to the hub. See lib/hub/fallback-headers.
  const headerSource = response?.headers.get(HUB_SOURCE_HEADER)
  if (headerSource === HUB_SOURCE_FALLBACK) {
    api.dispatch(
      recordHubStatus({
        ip,
        resource,
        source: 'postgres-fallback',
        staleAt: response?.headers.get(HUB_SYNCED_AT_HEADER) ?? null,
        hubError: null
      })
    )
    return
  }

  if (!data || typeof data !== 'object') return
  const env = data as Partial<HubReadEnvelope<unknown>>
  if (env.source !== 'hub' && env.source !== 'postgres-fallback') return

  api.dispatch(
    recordHubStatus({
      ip,
      resource,
      source: env.source,
      staleAt: env.staleAt ?? null,
      hubError: env.hubError ?? null
    })
  )
}

// Statuses where a second attempt can plausibly succeed: the server said
// "later" or the request never landed. Deliberately an ALLOW-list.
//
// 500 is NOT here, and that is the point. This used to be a deny-list
// (`400/401/403/404/422` fail fast, everything else retries), which made a 500
// retryable — so a deterministic server error became 4 requests plus 4 Redux
// dispatches per subscriber instead of one. Shell-mounted queries (the
// notification live provider, customer features) sit on every page, so a
// backend that is merely degraded turned into a re-render storm that tripped
// React's nested-update limit and blanked the whole page through
// `(apps)/error.tsx` ("Something went wrong!" — Sentry ATLAS-1J3, reproduced
// locally as React error #185 off a 500 on /api/notifications/preferences).
// A 500 is almost never transient; retrying it only amplifies the failure.
const RETRYABLE_HTTP_STATUSES = new Set([
  408, // Request Timeout
  429, // Too Many Requests
  502, // Bad Gateway
  503, // Service Unavailable
  504 // Gateway Timeout
])

// fetchBaseQuery's non-HTTP failures. FETCH_ERROR (network/DNS/offline) and
// TIMEOUT_ERROR are worth another go; PARSING_ERROR and CUSTOM_ERROR are
// deterministic — the same bytes will fail to parse the same way.
const RETRYABLE_ERROR_KINDS = new Set(['FETCH_ERROR', 'TIMEOUT_ERROR'])

function isTransientFailure(status: number | string): boolean {
  return typeof status === 'number'
    ? RETRYABLE_HTTP_STATUSES.has(status)
    : RETRYABLE_ERROR_KINDS.has(status)
}

/**
 * Every RTK write refused because the session is a read-only impersonation
 * says so once, in words, whatever the calling component's own error toast
 * says. The fixed id collapses a click that fans out to several writes into
 * one notice.
 */
function announceReadOnlyRefusal(error: FetchBaseQueryError) {
  const code = (error.data as { code?: unknown } | undefined)?.code
  if (error.status === 403 && code === READ_ONLY_IMPERSONATION_CODE) {
    toast.info(READ_ONLY_IMPERSONATION_MESSAGE, {
      id: READ_ONLY_IMPERSONATION_CODE
    })
  }
}

/**
 * Refuse a request for a guided tour's practice record before it is sent.
 *
 * Practice ids are never real, so a request ADDRESSED to one (path, query,
 * params) is refused whether or not a tour is running — that is what stops a
 * reload of a practice page from querying it. Bodies are checked only while a
 * tour runs: practice writes only happen then, and outside a tour a body leaf
 * equal to a practice id is someone's free text. Also while a tour runs and is
 * not paused, the endpoints its definition intercepts are refused: a paused
 * tour has left its pages, and blocking those writes app-wide would stop real
 * work until someone found the pill and exited. Returned as a CUSTOM_ERROR,
 * which isTransientFailure never retries.
 *
 * An intercepted write is a real button pressed mid-tour, so it says why
 * nothing happened, once (the fixed id), whatever the component's own error
 * toast says — even when its body also names a practice record, as a real
 * form filled from practice lists does. An intercepted read saves nothing to
 * speak of, and a practice-id refusal on any other request is a leak the page
 * should never have attempted; those only warn.
 */
function practiceRefusal(
  args: Parameters<typeof baseQuery>[0],
  api: Parameters<typeof baseQuery>[1]
): FetchBaseQueryError | null {
  const { url, body, params } =
    typeof args === 'string' ? { url: args, body: undefined, params: {} } : args
  const tour = selectActiveTour(api.getState() as RootState)
  const practiceId =
    urlMentionsPracticeId(url, params) ??
    (tour ? bodyMentionsPracticeId(body) : null)
  const intercepted =
    !!tour && !tour.suspended && tour.intercept.includes(api.endpoint)
  if (!practiceId && !intercepted) return null
  if (process.env.NODE_ENV !== 'production') {
    console.warn(
      `[tours] refused ${api.endpoint}: ${practiceId ? `names practice id ${practiceId}` : 'intercepted while a tour runs'}`
    )
  }
  if (intercepted && api.type === 'mutation') {
    toast.info(PRACTICE_INTERCEPTED_MESSAGE, { id: PRACTICE_BLOCKED_CODE })
  }
  return {
    status: 'CUSTOM_ERROR',
    error: 'Practice record: not sent',
    data: { code: PRACTICE_BLOCKED_CODE }
  }
}

export const retryBaseQuery = retry(
  async (args, api, extraOptions) => {
    const refused = practiceRefusal(args, api)
    if (refused) return retry.fail(refused)

    const result = await baseQuery(args, api, extraOptions)

    // If there's an error, check if we should retry or fail immediately
    if (result.error) {
      announceReadOnlyRefusal(result.error)
      // NEVER retry non-idempotent requests. A POST/PATCH/PUT/DELETE that
      // errors (or, worse, times out at the client after the server already
      // did the work) must not be silently re-fired — retrying dispatch,
      // work-order creation, notes, etc. duplicates the side effect (e.g. two
      // wm_tasks for one dispatch, ~5s apart).
      const method = (
        typeof args === 'object' && args.method ? args.method : 'GET'
      ).toUpperCase()
      const isIdempotent = method === 'GET' || method === 'HEAD'

      if (!isIdempotent || !isTransientFailure(result.error.status)) {
        // Stop retrying immediately.
        return retry.fail(result.error)
      }
    } else if (result.data) {
      maybeRecordHubStatus(args, api, result.data, result.meta?.response)
    }

    return result
  },
  {
    maxRetries: 3
  }
)
